import { useEffect, useRef, useState } from 'react'
import { Loader2, Pencil } from 'lucide-react'

// Inline-edit affordance for a single key/value cell in the
// PropertiesPanel. Shows the value as plain text by default; on
// hover reveals a tiny pencil icon. Clicking the pencil (or the
// value itself) swaps the cell for an input: a free-text input
// when `choices` is omitted, or a <select> dropdown when
// `choices` is supplied (used for IFC enums like PredefinedType).
// Enter or blur commits; Escape cancels.
//
// Designed to be drop-in replaceable for the static `<span>` that
// the read-only KeyValueTable would otherwise render, same outer
// dimensions, same font, only adds keyboard interaction.

interface Props {
  value: string
  /** Async save callback. Should resolve once the backend confirms;
   *  reject (throw) to surface an error inline. The component does
   *  not pre-validate: the server is the source of truth. */
  onSave: (next: string) => Promise<void>
  /** Optional tooltip on the pencil icon. */
  hint?: string
  /** Render the value with this trailing suffix (e.g. " m³" for
   *  quantity displays). Stripped before save so the user only
   *  edits the raw value. */
  suffix?: string
  /** When supplied, edit mode renders a <select> with these options
   *  (plus an implicit "(vazio)" entry that maps to an empty string for
   *  clearing). Otherwise edit mode is a free-text <input>. */
  choices?: readonly string[]
}

function EditableValue({ value, onSave, hint, suffix = '', choices }: Props) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const selectRef = useRef<HTMLSelectElement | null>(null)

  // Repor o rascunho é estado local derivado: adotado durante o render
  // com guarda sobre (editing, value), a mesma condição de disparo do
  // efeito anterior, para o comportamento ficar igual.
  const [prevEdit, setPrevEdit] = useState({ editing, value })
  if (prevEdit.editing !== editing || prevEdit.value !== value) {
    setPrevEdit({ editing, value })
    if (editing) {
      setDraft(value)
      setError(null)
    }
  }

  // O foco continua no efeito: é uma ação sobre o DOM, não estado.
  useEffect(() => {
    if (!editing) return
    // Defer focus so the input is mounted before we focus it.
    requestAnimationFrame(() => {
      if (choices) selectRef.current?.focus()
      else inputRef.current?.focus()
    })
  }, [editing, value, choices])

  const commit = async (overrideDraft?: string) => {
    if (saving) return
    const next = overrideDraft !== undefined ? overrideDraft : draft
    if (next === value) {
      setEditing(false)
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onSave(next)
      setEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  const cancel = () => {
    setDraft(value)
    setEditing(false)
    setError(null)
  }

  if (editing && choices) {
    // Dropdown editor: include an empty entry so the user can clear
    // the slot back to NULL. The current value is preselected even if
    // it's not in the official list (e.g. a USERDEFINED value already
    // in the file), so editing-then-cancelling never destroys data.
    const inList = choices.includes(draft)
    return (
      <span className="inline-flex w-full items-center gap-1">
        <select
          ref={selectRef}
          value={draft}
          onChange={(e) => {
            const v = e.target.value
            setDraft(v)
            // Commit on selection: keeps the UX tight; the user can
            // still press Escape from the keyboard before the option
            // is chosen by clicking.
            void commit(v)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              cancel()
            }
          }}
          onBlur={() => {
            // No commit on blur to avoid double-submit after change.
            if (!saving) setEditing(false)
          }}
          disabled={saving}
          className="flex-1 min-w-0 rounded border border-[var(--color-accent)] bg-[var(--color-bg)] px-1 py-px font-mono text-[11px] text-[var(--color-text)] disabled:opacity-50"
        >
          <option value="">(vazio)</option>
          {!inList && draft !== '' && (
            <option value={draft}>{draft} (custom)</option>
          )}
          {choices.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        {saving && (
          <Loader2 size={10} className="animate-spin text-[var(--color-accent)]" />
        )}
        {error && (
          <span title={error} className="text-[10px] text-red-400">
            erro
          </span>
        )}
      </span>
    )
  }

  if (editing) {
    return (
      <span className="inline-flex w-full items-center gap-1">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void commit()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              cancel()
            }
          }}
          onBlur={() => void commit()}
          disabled={saving}
          className="flex-1 min-w-0 rounded border border-[var(--color-accent)] bg-[var(--color-bg)] px-1 py-px font-mono text-[11px] text-[var(--color-text)] disabled:opacity-50"
        />
        {saving && (
          <Loader2 size={10} className="animate-spin text-[var(--color-accent)]" />
        )}
        {error && (
          <span title={error} className="text-[10px] text-red-400">
            erro
          </span>
        )}
      </span>
    )
  }

  return (
    <span
      className="group inline-flex w-full cursor-text items-center gap-1"
      onClick={() => setEditing(true)}
      title={hint ?? 'Clica para editar'}
    >
      <span className="flex-1 break-words">
        {value === '' ? (
          <span className="italic text-[var(--color-text-dim)]">vazio</span>
        ) : (
          value
        )}
        {suffix}
      </span>
      <Pencil
        size={9}
        className="shrink-0 opacity-0 transition-opacity group-hover:opacity-60"
      />
    </span>
  )
}

export default EditableValue
